import React, { useState } from 'react'
import axios from 'axios'


interface LayoutReviewApiRow {
  id: number;
  style_number?: string;
  customer_name?: string;
  submitted_date?: string;
  image_url?: string;
  sales_order_number?: string;
  brand_name?: string;
  brand_only?: boolean;
  cin7_customer_id?: string;
  cin7_sale_id?: string;
  Line_info: string;
  message: string;
  sku: string;
  active?: boolean;
  approved?: boolean;
}

type FormState = Omit<LayoutReviewApiRow, 'id'>;
type View = 'new' | 'edit' | null;

const emptyForm: FormState = {
  style_number: '',
  customer_name: '',
  submitted_date: '',
  image_url: '',
  sales_order_number: '',
  brand_name: '',
  brand_only: false,
  cin7_customer_id: '',
  cin7_sale_id: '',
  Line_info: '',
  message: '',
  sku: '',
  active: true,
};

const LayoutReview = () => {
  const [view, setView] = useState<View>('edit');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);

  const [lookupId, setLookupId] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<number | null>(null);

  const handleField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleUploadImage = () => {
    if (!window.wp?.media) {
      console.error('wp.media is not available (wp_enqueue_media() was not loaded on this page)');
      return;
    }

    const frame = window.wp.media({
      title: 'Select or upload an image',
      button: { text: 'Use this image' },
      multiple: false,
      library: { type: 'image' },
    });

    frame.on('select', () => {
      const attachment = frame.state().get('selection').first().toJSON();
      handleField('image_url', attachment.url);
    });

    frame.open();
  };

  const switchView = (next: View) => {
    setView(next);
    setForm(emptyForm);
    setError(null);
    setSavedId(null);
    setLookupId('');
    setLookupError(null);
    setLoadedId(null);
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (lookupLoading) return;
    if (!lookupId.trim()) {
      setLookupError('Enter an ID');
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setLoadedId(null);

    try {
      const res = await axios.post<{ success: boolean; row: LayoutReviewApiRow }>(
        window.tagabelData.root_url + '/wp-json/custom/v1/layout_get',
        { id: lookupId },
        { headers: { 'X-WP-Nonce': window.tagabelData?.nonce, 'x-custom-header': 'x-auth-token' } }
      );
      const row = res.data?.row;
      if (!res.data?.success || !row) {
        throw new Error('Layout not found here' , );
      }
      const toBool = (v: unknown) => v === true || v === 1 || v === '1';
      const { id, ...rest } = row;
      setForm({
        ...emptyForm,
        ...rest,
        active: toBool(rest.active),
        brand_only: toBool(rest.brand_only),
        approved: toBool(rest.approved),
      });
      setLoadedId(Number(id));
    } catch (err: any) {
      console.error('Lookup failed:', err);
      setLookupError(err?.response?.data?.message || err?.message || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    setSavedId(null);

    const isEdit = view === 'edit' && loadedId !== null;
    const url = window.tagabelData.root_url + (isEdit
      ? '/wp-json/custom/v1/layout_update'
      : '/wp-json/custom/v1/layout_post');
    const body = isEdit ? { id: loadedId, data: form } : { data: form };
    const request = isEdit ? axios.put : axios.post;

    try {
      const res = await request<{ success: boolean; id: number }>(
        url,
        body,
        { headers: { 'X-WP-Nonce': window.tagabelData?.nonce, 'x-custom-header': 'x-auth-token' } }
      );
      if (!res.data?.success || res.data?.id == null) {
        throw new Error('Unexpected response from server');
      }
      setSavedId(res.data.id);
      if (!isEdit) setForm(emptyForm);
    } catch (err: any) {
      console.error('Layout review submit failed:', err);
      setError(err?.response?.data?.message || err?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const btnClass = (active: boolean) =>
    `px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
      active
        ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
        : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'
    }`;

  const renderFields = () => (
    <>
      <div>
        <label className="block text-sm text-blue-600 pb-1">Style Number</label>
        <input
          type="text"
          className="users-input w-full"
          value={form.style_number}
          onChange={e => handleField('style_number', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Customer Name</label>
        <input
          type="text"
          className="users-input w-full"
          value={form.customer_name}
          onChange={e => handleField('customer_name', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Submitted Date</label>
        <input
          type="date"
          className="users-input w-full"
          value={form.submitted_date}
          onChange={e => handleField('submitted_date', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Image URL</label>
        <div className="flex gap-2 items-start">
          <input
            type="url"
            className="users-input w-full"
            value={form.image_url}
            onChange={e => handleField('image_url', e.target.value)}
          />
          <button
            type="button"
            className={btnClass(false)}
            onClick={handleUploadImage}
          >
            Upload
          </button>
        </div>
        {form.image_url && (
          <img
            src={form.image_url}
            alt=""
            className="mt-2 max-h-32 rounded border border-blue-100 object-contain"
          />
        )}
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Sales Order Number</label>
        <input
          type="text"
          className="users-input w-full"
          value={form.sales_order_number}
          onChange={e => handleField('sales_order_number', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Brand Name (of product)</label>
        <input
          type="text"
          className="users-input w-full"
          value={form.brand_name}
          onChange={e => handleField('brand_name', e.target.value)}
        />
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.brand_only}
          onChange={e => handleField('brand_only', e.target.checked)}
        />
        <span className="text-sm text-blue-600">For brand review only (if not will be for user with the following ID)</span>
      </label>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Cin7 Customer ID</label>
        <input
          type="text"
          className="users-input w-full"
          value={form.cin7_customer_id}
          onChange={e => handleField('cin7_customer_id', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Cin7 Sale ID</label>
        <input
          type="text"
          className="users-input w-full"
          value={form.cin7_sale_id}
          onChange={e => handleField('cin7_sale_id', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">SKU</label>
        <input
          type="text"
          required
          className="users-input w-full"
          value={form.sku}
          onChange={e => handleField('sku', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Product Line item to submit to cin7 as JSON data</label>
        <textarea
          required
          className="users-input w-full min-h-[80px]"
          value={form.Line_info}
          onChange={e => handleField('Line_info', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm text-blue-600 pb-1">Message</label>
        <textarea
          className="users-input w-full min-h-[80px]"
          value={form.message}
          onChange={e => handleField('message', e.target.value)}
        />
      </div>
    </>
  );

  return (
    <div className='flex justify-center items-center m-auto pt-8 flex-col'>
      <h2 className='text-4xl font-bold'>Layout Reviews</h2>

      <div className="bg-gray-200 mt-6 min-w-[600px] min-h-[400px] rounded-2xl p-6">
        <div className="flex gap-3">
          <button onClick={() => switchView('new')} className={btnClass(view === 'new')}>
            Add New Custom Layout
          </button>
          <button onClick={() => switchView('edit')} className={btnClass(view === 'edit')}>
            Edit Existing Layout
          </button>
        </div>

        {view === 'new' && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-6">
            <h3 className="text-2xl font-bold">New Custom Layout</h3>
            {renderFields()}

            {error && <p className="text-red-500 text-sm">{error}</p>}
            {savedId !== null && (
              <p className="text-green-600 text-sm">Saved — new layout ID: {savedId}</p>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </form>
        )}

        {view === 'edit' && (
          <div className="mt-6">
            <h3 className="text-2xl font-bold">Edit Existing Layout</h3>

            <form onSubmit={handleLookup} className="flex items-end gap-3 mt-4">
              <div className="flex-1">
                <label className="block text-sm text-blue-600 pb-1">Layout ID</label>
                <input
                  type="text"
                  className="users-input w-full"
                  value={lookupId}
                  onChange={e => setLookupId(e.target.value)}
                  placeholder="Enter layout ID"
                />
              </div>
              <button
                type="submit"
                disabled={lookupLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {lookupLoading ? 'Loading...' : 'Lookup'}
              </button>
            </form>
            {lookupError && <p className="text-red-500 text-sm mt-2">{lookupError}</p>}
            {loadedId !== null && (
              <p className="text-green-600 text-sm mt-2">Loaded layout ID: {loadedId}</p>
            )}

            {loadedId !== null && (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-6">
                {renderFields()}

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.active ?? false}
                    onChange={e => handleField('active', e.target.checked)}
                  />
                  <span className="text-sm text-blue-600">Active</span>
                </label>

                  <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.approved ?? false}
                    onChange={e => handleField('approved', e.target.checked)}
                  />
                  <span className="text-sm text-blue-600">approved</span>
                </label>

                {error && <p className="text-red-500 text-sm">{error}</p>}
                {savedId !== null && (
                  <p className="text-green-600 text-sm">Updated — layout ID: {savedId}</p>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LayoutReview;
